import type { SupportedPricingCurrencyType } from '@buggy/shared'
import type { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import Blur from '@/components/common/blur'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import useFormatPrice from '@/hooks/use-format-price'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Nullable } from '@/types/nullable'
import type { PriceRange } from '@/types/schema/kol/price-range-schema'

interface PostQuotationStatisticProps {
  loading?: boolean
  isKolInfoLocked: boolean
  showMockData?: boolean
  currencyCode: SupportedPricingCurrencyType
  quotationPriceRange?: Nullable<PriceRange>
}

export const PostQuotationStatistic: FunctionComponent<
  PostQuotationStatisticProps
> = ({
  loading,
  isKolInfoLocked,
  showMockData,
  currencyCode,
  quotationPriceRange,
}) => {
  const { formatMessage } = useIntl()
  const { formatPriceRange } = useFormatPrice()

  return (
    <Statistic
      loading={loading}
      title={formatMessage({ id: 'kol:post_quotations' })}
      tooltip={{
        title: formatMessage({ id: 'kol:quotation_tooltip' }),
      }}
      value={((): ReactNode => {
        if (isKolInfoLocked) {
          return (
            <Blur activate={!showMockData}>
              {formatPriceRange({
                minPrice: 500,
                maxPrice: 1000,
                currency: currencyCode,
              })}
            </Blur>
          )
        }

        return quotationPriceRange
          ? formatPriceRange({
              minPrice: quotationPriceRange.min_price,
              maxPrice: quotationPriceRange.max_price,
              currency: quotationPriceRange.currency,
            })
          : null
      })()}
    />
  )
}

const Statistic = styled(KolDetailStatistic)`
  flex: 1;
`
